import { c as _c } from "react/compiler-runtime";
import { DatePicker, toISOStringWithoutTimezone } from '@systemeio/components';
import { getCourseBundleAccessTypeDataOptions } from 'modules/courses/bundles/course-bundle/enums/access-type-enum';
import { getCourseAccessTypeDataOptions } from 'modules/courses/courses/classic-course/utils/get-course-access-type-data-options';
import { AccessTypeEnum } from 'modules/courses/courses/enums/access-type-enum';
import { isPartialType } from 'modules/courses/courses/utils/is-partial-type';
import { DigitalResourcesTypeEnum } from 'modules/digital-product/enums/digital-resources-type-enum';
import CourseModulesSkeleton from 'modules/digital-product/resources/components/course-module-skeleton';
import { useDigitalResources } from 'modules/digital-product/resources/hooks/use-digital-resources';
import { DigitalResourceEditType } from 'modules/digital-product/resources/types/digital-resource-edit-interface';
import { getGrantCourseAccessUrl } from 'modules/digital-product/resources/utils/get-grant-course-access-url';
import React from 'react';
import { Checkbox, CheckBoxSwitcher, FormInput, FormSelect, Hint } from 'shared/components';
import TransLoco from 'shared/components/trans-loco';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { getDatePickerMinDate } from 'shared/utils/get-date-picker-min-date';
import { getToday } from 'shared/utils/get-dates';

/**
 * Toggle rows keep the control at a fixed width and the switcher at its natural one, so turning a
 * toggle on never reflows the label next to it.
 */
const toggleRowClassName = 'flex min-h-[42px] flex-wrap items-center gap-5';
const toggleControlClassName = 'w-[200px] shrink-0';

/** Turning the access date toggle on preselects today, so an enabled toggle always has a date. */
const getTodayStart = (timezone?: string) => {
  const today = getToday(timezone);
  today.setHours(0, 0, 0, 0);
  return toISOStringWithoutTimezone(today);
};
export type DefinedDigitalResource = DigitalResourceEditType & {
  type: Required<DigitalResourceEditType>['type'];
};
interface ResourceFieldsProps {
  resource: DefinedDigitalResource;
  onChange: (data: DefinedDigitalResource) => void;
  errors?: Record<string, string[]>;
  keyPrefix?: string | number;
  /** Renders a label above every field; the inline list keeps placeholders only. */
  withLabels?: boolean;
}

/**
 * Fields of a single digital resource, shared by the inline resources list and the resource modal.
 */
export function ResourceFields(t0) {
  const $ = _c(181);
  const {
    resource,
    onChange,
    errors,
    keyPrefix: t1,
    withLabels
  } = t0;
  const keyPrefix = t1 === undefined ? 0 : t1;
  const {
    user,
    intlLocale
  } = useUser();
  const {
    t
  } = useLocoTranslation();
  const t2 = resource.type === DigitalResourcesTypeEnum.membership_course ? resource.course : undefined;
  const t3 = resource.type === DigitalResourcesTypeEnum.membership_course_bundle ? resource.courseBundle : undefined;
  let t4;
  if ($[0] !== resource.type || $[1] !== t2 || $[2] !== t3) {
    t4 = {
      resourceType: resource.type,
      courseId: t2,
      courseBundleId: t3
    };
    $[0] = resource.type;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const data = useDigitalResources(t4);
  switch (resource.type) {
    case DigitalResourcesTypeEnum.membership_course:
      {
        const t5 = `course-selector-${keyPrefix}`;
        let t6;
        if ($[4] !== t) {
          t6 = t("dashboard.funnel.configuration.common.offer_resources.choose_course");
          $[4] = t;
          $[5] = t6;
        } else {
          t6 = $[5];
        }
        let t7;
        if ($[6] !== t || $[7] !== withLabels) {
          t7 = withLabels ? t("dashboard.funnel.configuration.common.courses.title") : undefined;
          $[6] = t;
          $[7] = withLabels;
          $[8] = t7;
        } else {
          t7 = $[8];
        }
        let t8;
        if ($[9] !== onChange || $[10] !== resource.type) {
          t8 = value_6 => onChange({
            type: resource.type,
            course: value_6
          });
          $[9] = onChange;
          $[10] = resource.type;
          $[11] = t8;
        } else {
          t8 = $[11];
        }
        let t9;
        if ($[12] !== errors?.course) {
          t9 = errors?.course?.join(", ");
          $[12] = errors?.course;
          $[13] = t9;
        } else {
          t9 = $[13];
        }
        let t10;
        if ($[14] !== data.coursesData || $[15] !== resource.course || $[16] !== t5 || $[17] !== t6 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9 || $[21] !== withLabels) {
          t10 = <FormSelect key={t5} placeholder={t6} label={t7} required={withLabels} value={resource.course} data={data.coursesData} onChange={t8} error={t9} />;
          $[14] = data.coursesData;
          $[15] = resource.course;
          $[16] = t5;
          $[17] = t6;
          $[18] = t7;
          $[19] = t8;
          $[20] = t9;
          $[21] = withLabels;
          $[22] = t10;
        } else {
          t10 = $[22];
        }
        let t11;
        if ($[23] !== errors?.enrollmentAccessType || $[24] !== keyPrefix || $[25] !== onChange || $[26] !== resource.course || $[27] !== resource.enrollmentAccessType || $[28] !== resource.type || $[29] !== t || $[30] !== withLabels) {
          t11 = resource.course && <FormSelect key={`access-type-selector-${keyPrefix}`} placeholder={t("dashboard.funnel.configuration.common.offer_resources.choose_course_access_type")} label={withLabels ? t("dashboard.digital_product.resources.course.access_mode") : undefined} required={withLabels} value={resource.enrollmentAccessType || undefined} data={getCourseAccessTypeDataOptions(t)} onChange={value_7 => onChange({
            type: resource.type,
            enrollmentAccessType: value_7,
            accessFrom: undefined,
            expirationPeriod: undefined,
            modules: []
          })} error={errors?.enrollmentAccessType?.join(", ")} />;
          $[23] = errors?.enrollmentAccessType;
          $[24] = keyPrefix;
          $[25] = onChange;
          $[26] = resource.course;
          $[27] = resource.enrollmentAccessType;
          $[28] = resource.type;
          $[29] = t;
          $[30] = withLabels;
          $[31] = t11;
        } else {
          t11 = $[31];
        }
        let t12;
        if ($[32] !== data.courseModulesData || $[33] !== keyPrefix || $[34] !== onChange || $[35] !== resource.enrollmentAccessType || $[36] !== resource.modules || $[37] !== resource.type) {
          t12 = resource.enrollmentAccessType && isPartialType(resource.enrollmentAccessType) && <div key={`modules-selector-${keyPrefix}`} className={`flex-col gap-1 text-sm font-medium ${data.courseModulesData && data.courseModulesData.length === 0 ? "hidden" : "flex"}`}><ul className="flex flex-row flex-wrap gap-5">{data.courseModulesData ? data.courseModulesData.map(module => {
                const isSelected = !!resource.modules?.find(el => el === module.id);
                return <li key={module.id}><Checkbox value={isSelected} label={module.caption} onChange={() => onChange({
                    type: resource.type,
                    modules: isSelected ? resource.modules?.filter(el_0 => el_0 !== module.id) : [...(resource.modules || []), module.id]
                  })} /></li>;
              }) : <CourseModulesSkeleton />}</ul></div>;
          $[32] = data.courseModulesData;
          $[33] = keyPrefix;
          $[34] = onChange;
          $[35] = resource.enrollmentAccessType;
          $[36] = resource.modules;
          $[37] = resource.type;
          $[38] = t12;
        } else {
          t12 = $[38];
        }
        let t13;
        if ($[39] !== intlLocale || $[40] !== keyPrefix || $[41] !== onChange || $[42] !== resource.accessFrom || $[43] !== resource.enrollmentAccessType || $[44] !== resource.type || $[45] !== t || $[46] !== user?.createdAt || $[47] !== user?.dashboardLocale || $[48] !== user?.timezone) {
          t13 = resource.enrollmentAccessType && <div className={toggleRowClassName} key={`access-from-selector-${keyPrefix}`}><div className="min-w-fit"><CheckBoxSwitcher label={<Hint label={<TransLoco t={t} i18nKey="dashboard.funnel.configuration.common.offer_resources.course_start_access_on_day_description" components={{
                a: <a target="_blank" className="link" href={getGrantCourseAccessUrl(user?.dashboardLocale)} rel="noreferrer" />
              }} />}>{t("dashboard.funnel.configuration.common.offer_resources.course_start_access_on_day")}</Hint>} selected={resource.accessFrom != null} onChange={value_8 => onChange({
                type: resource.type,
                accessFrom: value_8 ? getTodayStart(user?.timezone) : undefined
              })} /></div>{resource.accessFrom != null && <div className={toggleControlClassName}><DatePicker value={resource.accessFrom} minDate={getDatePickerMinDate(user?.createdAt, user?.timezone, getToday(user?.timezone))} onChange={value_9 => onChange({
                type: resource.type,
                accessFrom: value_9
              })} timezone={user?.timezone} intlLocale={intlLocale} /></div>}</div>;
          $[39] = intlLocale;
          $[40] = keyPrefix;
          $[41] = onChange;
          $[42] = resource.accessFrom;
          $[43] = resource.enrollmentAccessType;
          $[44] = resource.type;
          $[45] = t;
          $[46] = user?.createdAt;
          $[47] = user?.dashboardLocale;
          $[48] = user?.timezone;
          $[49] = t13;
        } else {
          t13 = $[49];
        }
        let t14;
        if ($[50] !== keyPrefix || $[51] !== onChange || $[52] !== resource.enrollmentAccessType || $[53] !== resource.expirationPeriod || $[54] !== resource.type || $[55] !== t) {
          t14 = (resource.enrollmentAccessType === AccessTypeEnum.fullAccess || resource.enrollmentAccessType === AccessTypeEnum.partialAccess) && <div className={toggleRowClassName} key={`enrollment-access-type-selector-${keyPrefix}`}><div className="min-w-fit"><CheckBoxSwitcher label={<Hint label={t("dashboard.funnel.configuration.common.offer_resources.course_expiration_period_popover")}>{t("dashboard.funnel.configuration.common.offer_resources.course_expiration_period")}</Hint>} selected={resource.expirationPeriod != null} onChange={value_10 => onChange({
                type: resource.type,
                expirationPeriod: value_10 ? 30 : undefined
              })} /></div>{resource.expirationPeriod != null && <FormInput className={toggleControlClassName} key={`expiration-period-selector-${keyPrefix}`} value={resource.expirationPeriod} onChange={e => {
              let updatedValue = e.target.valueAsNumber;
              if (updatedValue < 0 || isNaN(updatedValue)) {
                updatedValue = 0;
              }
              onChange({
                type: resource.type,
                expirationPeriod: updatedValue
              });
            }} type="number" min={0} />}</div>;
          $[50] = keyPrefix;
          $[51] = onChange;
          $[52] = resource.enrollmentAccessType;
          $[53] = resource.expirationPeriod;
          $[54] = resource.type;
          $[55] = t;
          $[56] = t14;
        } else {
          t14 = $[56];
        }
        let t15;
        if ($[57] !== t10 || $[58] !== t11 || $[59] !== t12 || $[60] !== t13 || $[61] !== t14) {
          t15 = <>{t10}{t11}{t12}{t13}{t14}</>;
          $[57] = t10;
          $[58] = t11;
          $[59] = t12;
          $[60] = t13;
          $[61] = t14;
          $[62] = t15;
        } else {
          t15 = $[62];
        }
        return t15;
      }
    case DigitalResourcesTypeEnum.membership_course_bundle:
      {
        const t5 = `course-bundle-selector-${keyPrefix}`;
        let t6;
        if ($[63] !== t) {
          t6 = t("dashboard.funnel.configuration.common.offer_resources.choose_course_bundle");
          $[63] = t;
          $[64] = t6;
        } else {
          t6 = $[64];
        }
        let t7;
        if ($[65] !== t || $[66] !== withLabels) {
          t7 = withLabels ? t("dashboard.funnel.configuration.common.course_bundles.title") : undefined;
          $[65] = t;
          $[66] = withLabels;
          $[67] = t7;
        } else {
          t7 = $[67];
        }
        let t8;
        if ($[68] !== onChange || $[69] !== resource.type) {
          t8 = value_3 => onChange({
            type: resource.type,
            courseBundle: value_3,
            enrollmentAccessType: null,
            enrollmentDrippingAccessCourse: undefined
          });
          $[68] = onChange;
          $[69] = resource.type;
          $[70] = t8;
        } else {
          t8 = $[70];
        }
        let t9;
        if ($[71] !== errors?.courseBundle) {
          t9 = errors?.courseBundle?.join(", ");
          $[71] = errors?.courseBundle;
          $[72] = t9;
        } else {
          t9 = $[72];
        }
        let t10;
        if ($[73] !== data.courseBundlesData || $[74] !== resource.courseBundle || $[75] !== t5 || $[76] !== t6 || $[77] !== t7 || $[78] !== t8 || $[79] !== t9 || $[80] !== withLabels) {
          t10 = <FormSelect key={t5} placeholder={t6} label={t7} required={withLabels} value={resource.courseBundle} data={data.courseBundlesData} onChange={t8} error={t9} />;
          $[73] = data.courseBundlesData;
          $[74] = resource.courseBundle;
          $[75] = t5;
          $[76] = t6;
          $[77] = t7;
          $[78] = t8;
          $[79] = t9;
          $[80] = withLabels;
          $[81] = t10;
        } else {
          t10 = $[81];
        }
        let t11;
        if ($[82] !== errors?.enrollmentAccessType || $[83] !== keyPrefix || $[84] !== onChange || $[85] !== resource.courseBundle || $[86] !== resource.enrollmentAccessType || $[87] !== resource.type || $[88] !== t || $[89] !== withLabels) {
          t11 = resource.courseBundle && <FormSelect key={`course-bundle-access-type-selector-${keyPrefix}`} placeholder={t("dashboard.funnel.configuration.common.offer_resources.choose_course_bundle_access_type")} label={withLabels ? t("dashboard.digital_product.resources.course.access_mode") : undefined} required={withLabels} value={resource.enrollmentAccessType || undefined} data={getCourseBundleAccessTypeDataOptions(t)} onChange={value_4 => onChange({
            type: resource.type,
            enrollmentAccessType: value_4,
            enrollmentDrippingAccessCourse: undefined
          })} error={errors?.enrollmentAccessType?.join(", ")} />;
          $[82] = errors?.enrollmentAccessType;
          $[83] = keyPrefix;
          $[84] = onChange;
          $[85] = resource.courseBundle;
          $[86] = resource.enrollmentAccessType;
          $[87] = resource.type;
          $[88] = t;
          $[89] = withLabels;
          $[90] = t11;
        } else {
          t11 = $[90];
        }
        let t12;
        if ($[91] !== data.courseBundleCoursesData || $[92] !== errors?.enrollmentDrippingAccessCourse || $[93] !== keyPrefix || $[94] !== onChange || $[95] !== resource.courseBundle || $[96] !== resource.enrollmentAccessType || $[97] !== resource.enrollmentDrippingAccessCourse || $[98] !== resource.type || $[99] !== t) {
          t12 = resource.courseBundle && resource.enrollmentAccessType === AccessTypeEnum.drippingContent && !!resource.enrollmentDrippingAccessCourse && <FormSelect key={`enrollment-dripping-course-selector-${keyPrefix}`} placeholder={t("dashboard.funnel.configuration.common.offer_resources.choose_dripping_course")} value={resource.enrollmentDrippingAccessCourse} data={data.courseBundleCoursesData} onChange={value_5 => onChange({
            type: resource.type,
            enrollmentDrippingAccessCourse: value_5
          })} error={errors?.enrollmentDrippingAccessCourse?.join(", ")} />;
          $[91] = data.courseBundleCoursesData;
          $[92] = errors?.enrollmentDrippingAccessCourse;
          $[93] = keyPrefix;
          $[94] = onChange;
          $[95] = resource.courseBundle;
          $[96] = resource.enrollmentAccessType;
          $[97] = resource.enrollmentDrippingAccessCourse;
          $[98] = resource.type;
          $[99] = t;
          $[100] = t12;
        } else {
          t12 = $[100];
        }
        let t13;
        if ($[101] !== t10 || $[102] !== t11 || $[103] !== t12) {
          t13 = <>{t10}{t11}{t12}</>;
          $[101] = t10;
          $[102] = t11;
          $[103] = t12;
          $[104] = t13;
        } else {
          t13 = $[104];
        }
        return t13;
      }
    case DigitalResourcesTypeEnum.systemeio_plan:
      {
        const t5 = `systemeio-plan-selector-${keyPrefix}`;
        let t6;
        if ($[105] !== t) {
          t6 = t("dashboard.funnel.configuration.common.offer_resources.choose_systeme_io_plan");
          $[105] = t;
          $[106] = t6;
        } else {
          t6 = $[106];
        }
        let t7;
        if ($[107] !== t || $[108] !== withLabels) {
          t7 = withLabels ? t("dashboard.funnel.configuration.common.user_plans.title") : undefined;
          $[107] = t;
          $[108] = withLabels;
          $[109] = t7;
        } else {
          t7 = $[109];
        }
        let t8;
        if ($[110] !== onChange || $[111] !== resource.type) {
          t8 = value_2 => onChange({
            type: resource.type,
            value: value_2
          });
          $[110] = onChange;
          $[111] = resource.type;
          $[112] = t8;
        } else {
          t8 = $[112];
        }
        let t9;
        if ($[113] !== errors?.value) {
          t9 = errors?.value?.join(", ");
          $[113] = errors?.value;
          $[114] = t9;
        } else {
          t9 = $[114];
        }
        let t10;
        if ($[115] !== data.plansData || $[116] !== resource.value || $[117] !== t5 || $[118] !== t6 || $[119] !== t7 || $[120] !== t8 || $[121] !== t9 || $[122] !== withLabels) {
          t10 = <FormSelect key={t5} placeholder={t6} label={t7} required={withLabels} value={resource.value} data={data.plansData} onChange={t8} error={t9} />;
          $[115] = data.plansData;
          $[116] = resource.value;
          $[117] = t5;
          $[118] = t6;
          $[119] = t7;
          $[120] = t8;
          $[121] = t9;
          $[122] = withLabels;
          $[123] = t10;
        } else {
          t10 = $[123];
        }
        return t10;
      }
    case DigitalResourcesTypeEnum.systemeio_tag:
      {
        const t5 = `tag-selector-${keyPrefix}`;
        let t6;
        if ($[124] !== t) {
          t6 = t("dashboard.funnel.configuration.common.offer_resources.choose_tag");
          $[124] = t;
          $[125] = t6;
        } else {
          t6 = $[125];
        }
        let t7;
        if ($[126] !== t || $[127] !== withLabels) {
          t7 = withLabels ? t("dashboard.funnel.configuration.common.tags.title") : undefined;
          $[126] = t;
          $[127] = withLabels;
          $[128] = t7;
        } else {
          t7 = $[128];
        }
        let t8;
        if ($[129] !== onChange || $[130] !== resource.type) {
          t8 = value_1 => onChange({
            type: resource.type,
            value: value_1
          });
          $[129] = onChange;
          $[130] = resource.type;
          $[131] = t8;
        } else {
          t8 = $[131];
        }
        let t9;
        if ($[132] !== errors?.value) {
          t9 = errors?.value?.join(", ");
          $[132] = errors?.value;
          $[133] = t9;
        } else {
          t9 = $[133];
        }
        let t10;
        if ($[134] !== data.tagsData || $[135] !== resource.value || $[136] !== t5 || $[137] !== t6 || $[138] !== t7 || $[139] !== t8 || $[140] !== t9 || $[141] !== withLabels) {
          t10 = <FormSelect key={t5} placeholder={t6} label={t7} required={withLabels} value={resource.value} data={data.tagsData} onChange={t8} error={t9} />;
          $[134] = data.tagsData;
          $[135] = resource.value;
          $[136] = t5;
          $[137] = t6;
          $[138] = t7;
          $[139] = t8;
          $[140] = t9;
          $[141] = withLabels;
          $[142] = t10;
        } else {
          t10 = $[142];
        }
        return t10;
      }
    case DigitalResourcesTypeEnum.community_access:
      {
        const t5 = `community-access-selector-${keyPrefix}`;
        let t6;
        if ($[143] !== t) {
          t6 = t("dashboard.funnel.configuration.common.offer_resources.choose_community_access");
          $[143] = t;
          $[144] = t6;
        } else {
          t6 = $[144];
        }
        let t7;
        if ($[145] !== t || $[146] !== withLabels) {
          t7 = withLabels ? t("dashboard.funnel.configuration.common.communities.title") : undefined;
          $[145] = t;
          $[146] = withLabels;
          $[147] = t7;
        } else {
          t7 = $[147];
        }
        let t8;
        if ($[148] !== onChange || $[149] !== resource.type) {
          t8 = value_0 => onChange({
            type: resource.type,
            value: value_0
          });
          $[148] = onChange;
          $[149] = resource.type;
          $[150] = t8;
        } else {
          t8 = $[150];
        }
        let t9;
        if ($[151] !== errors?.value) {
          t9 = errors?.value?.join(", ");
          $[151] = errors?.value;
          $[152] = t9;
        } else {
          t9 = $[152];
        }
        let t10;
        if ($[153] !== data.communitiesData || $[154] !== resource.value || $[155] !== t5 || $[156] !== t6 || $[157] !== t7 || $[158] !== t8 || $[159] !== t9 || $[160] !== withLabels) {
          t10 = <FormSelect key={t5} placeholder={t6} label={t7} required={withLabels} value={resource.value} data={data.communitiesData} onChange={t8} error={t9} />;
          $[153] = data.communitiesData;
          $[154] = resource.value;
          $[155] = t5;
          $[156] = t6;
          $[157] = t7;
          $[158] = t8;
          $[159] = t9;
          $[160] = withLabels;
          $[161] = t10;
        } else {
          t10 = $[161];
        }
        return t10;
      }
    case DigitalResourcesTypeEnum.booking_event:
      {
        const t5 = `booking-event-selector-${keyPrefix}`;
        let t6;
        if ($[162] !== t) {
          t6 = t("dashboard.digital_product.resources.booking_events.choose_event");
          $[162] = t;
          $[163] = t6;
        } else {
          t6 = $[163];
        }
        let t7;
        if ($[164] !== t || $[165] !== withLabels) {
          t7 = withLabels ? t("dashboard.digital_product.resources.booking_events.title") : undefined;
          $[164] = t;
          $[165] = withLabels;
          $[166] = t7;
        } else {
          t7 = $[166];
        }
        let t8;
        if ($[167] !== onChange || $[168] !== resource.type) {
          t8 = value => onChange({
            type: resource.type,
            value
          });
          $[167] = onChange;
          $[168] = resource.type;
          $[169] = t8;
        } else {
          t8 = $[169];
        }
        let t9;
        if ($[170] !== errors?.value) {
          t9 = errors?.value?.join(", ");
          $[170] = errors?.value;
          $[171] = t9;
        } else {
          t9 = $[171];
        }
        let t10;
        if ($[172] !== data.eventsData || $[173] !== resource.value || $[174] !== t5 || $[175] !== t6 || $[176] !== t7 || $[177] !== t8 || $[178] !== t9 || $[179] !== withLabels) {
          t10 = <FormSelect key={t5} placeholder={t6} label={t7} required={withLabels} value={resource.value} data={data.eventsData} onChange={t8} error={t9} />;
          $[172] = data.eventsData;
          $[173] = resource.value;
          $[174] = t5;
          $[175] = t6;
          $[176] = t7;
          $[177] = t8;
          $[178] = t9;
          $[179] = withLabels;
          $[180] = t10;
        } else {
          t10 = $[180];
        }
        return t10;
      }
    default:
      {
        return null;
      }
  }
}